import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import CommonAnimatedLoader from '../../components/common/animated/loader';
import CommonButtonDefault from '../../components/common/buttons/default';
import { useAuth } from '../../hooks/use-auth';
import { Location } from '../../entities/location';
import * as locationsService from '../../services/api/locations';

function LocationsListPageComponent() {
  const { token } = useAuth();
  const [items, setItems] = useState<Location[]>([]);
  const [locationsById, setLocationsById] = useState<Record<number, Location>>({});
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [deletingId, setDeletingId] = useState<number | null>(null);

  useEffect(() => {
    const fetchLocations = async () => {
      setIsLoading(true);

      try {
        const response = await locationsService.list(token);
        const locations = response || [];
        const locationIndex = locations.reduce<Record<number, Location>>((acc, location) => {
          acc[location.id] = location;

          return acc;
        }, {});

        setLocationsById(locationIndex);
        setItems(
          locations.filter((location) => location.locationParentId !== null && location.locationParentId !== undefined),
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchLocations();
  }, [token]);

  const handleDelete = async (id: number) => {
    // eslint-disable-next-line no-alert
    const isConfirmed = window.confirm('Are you sure you want to delete this location?');

    if (!isConfirmed) {
      return;
    }

    setDeletingId(id);

    try {
      await locationsService.remove(token, id);

      setItems((prevItems) => prevItems.filter((item) => item.id !== id));
      setLocationsById((prevLocations) => {
        const nextLocations = { ...prevLocations };

        delete nextLocations[id];

        return nextLocations;
      });
    } catch (error) {
      // eslint-disable-next-line no-alert
      alert('Failed to delete location. Please try again.');
    } finally {
      setDeletingId(null);
    }
  };

  const filteredItems = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    if (!query) {
      return items;
    }

    return items.filter((item) => {
      const displayTitle = item.partnerName ? `${item.partnerName} ${item.title}` : item.title;
      const parentId = item.locationParentId;
      const cityTitle = parentId !== null && parentId !== undefined ? locationsById[parentId]?.title || '' : '';

      return displayTitle.toLowerCase().includes(query) || (!!cityTitle && cityTitle.toLowerCase().includes(query));
    });
  }, [items, locationsById, searchQuery]);

  return (
    <div>
      <header className="mb-3 mt-3">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-4">
          <div className="flex flex-col gap-4 md:flex-row md:items-center">
            <h1 className="text-3xl font-bold leading-tight text-gray-900">Locations</h1>
            <form className="w-full md:max-w-sm md:ml-auto" onSubmit={(event) => event.preventDefault()}>
              <label className="sr-only" htmlFor="locations-search">
                Search locations
              </label>
              <input
                id="locations-search"
                type="search"
                className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                placeholder="Search by partner or city"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
              />
            </form>
            <Link className="md:ml-4" to="/locations/create">
              <CommonButtonDefault primary>Create new</CommonButtonDefault>
            </Link>
          </div>
        </div>
      </header>
      <main>
        <div className="max-w-7xl mx-auto sm:px-6 lg:px-8">
          {isLoading && <CommonAnimatedLoader />}
          {!isLoading && (
            <div className="bg-white shadow overflow-hidden sm:rounded-md">
              {!filteredItems.length && (
                <div className="p-6 text-sm text-gray-500">
                  {searchQuery ? 'No locations match your search.' : 'No locations found.'}
                </div>
              )}
              {!!filteredItems.length && (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Title
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        City
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Updated
                      </th>
                      <th className="px-6 py-3">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredItems.map((item) => {
                      const displayTitle = item.partnerName ? `${item.partnerName} ${item.title}` : item.title;
                      const parentId = item.locationParentId;
                      const cityTitle =
                        parentId !== null && parentId !== undefined ? locationsById[parentId]?.title || '-' : '-';

                      return (
                        <tr key={`location-${item.id}`} className="hover:bg-gray-50">
                          <td className="px-6 py-4 text-sm font-medium text-gray-900">{displayTitle}</td>
                          <td className="px-6 py-4 text-sm text-gray-500">{cityTitle}</td>
                          <td className="px-6 py-4 text-sm text-gray-500">{item.status || '-'}</td>
                          <td className="px-6 py-4 text-sm text-gray-500">
                            {item.updatedAt ? new Date(item.updatedAt).toLocaleString() : '-'}
                          </td>
                          <td className="px-6 py-4 text-right text-sm font-medium">
                            <Link className="text-indigo-600 hover:text-indigo-900" to={`/locations/edit/${item.id}`}>
                              Edit
                            </Link>
                            <button
                              type="button"
                              className="ml-4 text-red-600 hover:text-red-900 disabled:opacity-50"
                              onClick={() => handleDelete(item.id)}
                              disabled={deletingId === item.id}
                            >
                              {deletingId === item.id ? 'Deleting...' : 'Delete'}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

export default LocationsListPageComponent;
